/// <amd-module name="TFS/DRITools/Components/MainView" />
import * as React from "react";

// Office Fabric
import { SelectionMode } from "OfficeFabric/Selection";
import { Callout } from "VSSUI/Callout";

// VSSUI
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Hub } from "VSSUI/Hub";
import { HubHeader } from "VSSUI/HubHeader";
import { HistoryBehavior } from "VSSUI/Navigation/ViewStateNavigation";
import { VssHubViewState } from "VSSUI/Navigation/VssHubViewState";
import { IPickListItem, IPickListSelection, PickList } from "VSSUI/PickList";
import { PivotBarItem, PivotBarViewActionType } from "VSSUI/PivotBar";
import { Location } from "VSSUI/Utilities/Position";
import { VIEW_OPTIONS_CHANGE_EVENT } from "VSSUI/Utilities/ViewOptions";
import { VssIconType } from "VSSUI/VssIcon";

// DRI Tools Components
import CallgraphView from "TFS/DRITools/Components/Callgraph/CallgraphView";
import UsageView from "TFS/DRITools/Components/UsageInsights/UsageView";

// Resources
import * as Resources from "TFS/DRITools/Resources";
import { knownActivityLogColumns } from "TFS/DRITools/util/Columns";

export interface IMainViewState extends IVssComponentProperties {
    isCalloutVisible: boolean;
    selectedColumns: string[];
}

class MainView extends VssComponent<{}, IMainViewState> {
    private _hubViewState: VssHubViewState;
    private _possibleColumns: string[];
    private _columnSelectionButton: HTMLElement;

    private _initialViewOptionState = {
        onOff1: true,
        choiceGroup1: "cgOption13",
        slider1: 200
    };

    private _inputChange = {
        selectedColumns: (value: IPickListSelection): void => {
            this.setState({
                selectedColumns: value.selectedItems
            });
        },
        isCalloutVisible: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | undefined, value: boolean): void => {
            if (!this._columnSelectionButton && event && event.target instanceof Element) {
                this._columnSelectionButton = event.target as HTMLElement;
            }
            this.setState({
                isCalloutVisible: value
            });
        }
    };

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        this.state = {
            isCalloutVisible: false,
            selectedColumns: [
                Resources.Callgraph_ScaleUnit,
                Resources.Callgraph_ActivityId,
                Resources.Callgraph_StartTime,
                Resources.Callgraph_Command,
                Resources.Callgraph_ResponseCode,
                Resources.Callgraph_ActivityStatus
            ]
        };
        this._possibleColumns = knownActivityLogColumns;

        this._hubViewState = new VssHubViewState(context.pageContext, {
            defaultPivot: "Callgraph",
            viewOptions: {
                initialState: this._initialViewOptionState
            },
            viewOptionNavigationParameters: [{ key: "id", rawString: true, behavior: HistoryBehavior.newEntry }],
            filterNavigationParameters: [{ key: "keyword", rawString: true }, { key: "colorFilter" }]
        });

        this._hubViewState.selectedPivot.subscribe(this._forceUpdate);
        this._hubViewState.viewOptions.subscribe(this._forceUpdate, VIEW_OPTIONS_CHANGE_EVENT);
    }

    public render(): JSX.Element {
        const { isCalloutVisible, selectedColumns } = this.state;

        return (
            <Hub className="absolute-fill dri-tools-common" hubViewState={this._hubViewState}>
                <HubHeader title={Resources.Callgraph_PageTitle} />
                <PivotBarItem
                    name="Callgraph"
                    itemKey={"Callgraph"}
                    groupKey="group1"
                    viewActions={[
                        {
                            key: "columnOptions",
                            name: "Column Options",
                            actionType: PivotBarViewActionType.Command,
                            iconProps: { iconName: "Repair", iconType: VssIconType.fabric },
                            important: true,
                            onClick: (ev: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | undefined) =>
                                this._inputChange.isCalloutVisible(ev, !this.state.isCalloutVisible)
                        }
                    ]}
                >
                    {isCalloutVisible && (
                        <Callout
                            anchorElement={this._columnSelectionButton}
                            anchorOrigin={{ horizontal: Location.start, vertical: Location.end }}
                            calloutOrigin={{ horizontal: Location.start, vertical: Location.start }}
                            contentClassName="picklist-columns"
                            contentShadow={true}
                            focuszoneProps={{ focusOnMount: true, includeDefaults: true }}
                            onDismiss={() => this._inputChange.isCalloutVisible(undefined, false)}
                            role={"alertdialog"}
                        >
                            <div className="vss-PickList picklist-columns">
                                <PickList
                                    getListItem={this._getListItem}
                                    items={this._possibleColumns}
                                    initiallySelectedItems={selectedColumns}
                                    onSelectionChanged={this._inputChange.selectedColumns}
                                    selectionMode={SelectionMode.multiple}
                                />
                            </div>
                        </Callout>
                    )}
                    <CallgraphView selectedColumns={selectedColumns} />
                </PivotBarItem>
                <PivotBarItem name="Usage Details" itemKey="Usage" groupKey="group2">
                    <UsageView />
                </PivotBarItem>
            </Hub>
        );
    }

    private _forceUpdate = (): void => {
        this.forceUpdate();
    };

    private _getListItem = (item: string): IPickListItem => {
        return {
            name: item,
            key: item,
            iconProps: {
                iconType: VssIconType.bowtie,
                iconName: "view-list"
            }
        };
    };
}

Components.add("dri-tool-mainView", MainView);
